import { Exception, IArgumentType } from '@ch-types/Exception';
import { SeawardException } from '@src/types';

export interface IAlternativeBotExceptionArgumentType {
    message: string;
    extraData: {
        data: string;
        botName: string;
    };
}

/**
 * Base Exception for all Voice Exceptions.
 */
export class VoiceException extends SeawardException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'VoiceException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): VoiceException {
        return new VoiceException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when weblab is not enabled for a store/user.
 */
export class VoiceNotEnabledException extends SeawardException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'VoiceNotEnabledException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): VoiceNotEnabledException {
        return new VoiceNotEnabledException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when a dependency of the voice framework fails.
 */
export class VoiceDependencyFailureException extends VoiceException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'VoiceDependencyFailureException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): VoiceDependencyFailureException {
        return new VoiceDependencyFailureException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when the browser doesn't support an API and we try to call it.
 * Note that this is not automatically thrown by the browser (you have to throw it yourself).
 */
export class UnsupportedBrowserException extends SeawardException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'UnsupportedBrowserException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): UnsupportedBrowserException {
        return new UnsupportedBrowserException({
            message: obj.message,
        });
    }
}

/**
 * Mimics the InvalidRequestException for local APIs that live within the base tool realm.
 */
export class InvalidInputException extends SeawardException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'InvalidInputException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): InvalidInputException {
        return new InvalidInputException({
            message: obj.message,
        });
    }
}

/**
 * Base Exception thrown when there is a problem playing audio.
 */
export class AudioException extends SeawardException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'AudioException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): AudioException {
        return new AudioException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when the audio playing is interrupted by an audio request that has the same or
 * equal priority.
 */
export class InterruptedAudioException extends AudioException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'InterruptedAudioException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): InterruptedAudioException {
        return new InterruptedAudioException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when there is already audio playing that has a higher priority than the requested
 * one.
 */
export class HigherPriorityAudioPlayingException extends AudioException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'HigherPriorityAudioPlayingException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): HigherPriorityAudioPlayingException {
        return new HigherPriorityAudioPlayingException({
            message: obj.message,
        });
    }
}

/**
 * Base Exception thrown when there is a problem recording audio.
 */
export class AudioRecordingException extends SeawardException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'AudioRecordingException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): AudioRecordingException {
        return new AudioRecordingException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when you try to start recording while there was a recording already happening.
 */
export class RecordingAlreadyInProgressException extends AudioRecordingException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'RecordingAlreadyInProgressException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): RecordingAlreadyInProgressException {
        return new RecordingAlreadyInProgressException({
            message: obj.message,
        });
    }
}

/**
 * Base Exception thrown when there is a problem processing audio.
 */
export class AudioProcessingException extends SeawardException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'AudioProcessingException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): AudioProcessingException {
        return new AudioProcessingException({
            message: obj.message,
        });
    }
}

/**
 * Base Exception thrown when there's a problem with SpeechRecognitionException.
 */
export class SpeechRecognitionException extends SeawardException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'SpeechRecognitionException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): SpeechRecognitionException {
        return new SpeechRecognitionException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when an unexpected intent is retruned from Lex. This can happen if the Lex bot has a new
 * intent that the client doesn't know about.
 */
export class UnexpectedIntentException extends SpeechRecognitionException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'UnexpectedIntentException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): UnexpectedIntentException {
        return new UnexpectedIntentException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when Lex returns with a repeat intent. This means that the user asked Lex to repeat,
 * and the caller should handle this.
 */
export class RepeatIntentException extends UnexpectedIntentException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'RepeatIntentException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): RepeatIntentException {
        return new RepeatIntentException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when Lex returns with a stop intent. This means that the user asked Lex to stop,
 * and the caller should handle this.
 */
export class StopIntentException extends UnexpectedIntentException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'StopIntentException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): StopIntentException {
        return new StopIntentException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when Lex returns with a pause intent. This means that the user asked Lex to pause,
 * and the caller should handle this.
 */
export class PauseIntentException extends UnexpectedIntentException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'PauseIntentException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): PauseIntentException {
        return new PauseIntentException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when the speech recognition service was not able to extract the desired data from the audio.
 * This can be handled by prompting the user to try again.
 */
export class InvalidSpeechException extends SpeechRecognitionException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'InvalidSpeechException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): InvalidSpeechException {
        return new InvalidSpeechException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when the data returned by Lex fails local validation (i.e expected an integer and got a float).
 * This can be handled by prompting the user try again.
 */
export class InvalidSpeechInputException extends InvalidSpeechException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'InvalidSpeechInputException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): InvalidSpeechInputException {
        return new InvalidSpeechInputException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when Lex is unable to understand. This can be handled by prompting the user to try again.
 */
export class UnrecognizedSpeechException extends InvalidSpeechException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'UnrecognizedSpeechException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): UnrecognizedSpeechException {
        return new UnrecognizedSpeechException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when an alternative bot matches instead of the primary bot.
 */
export class AlternativeBotException extends VoiceException {
    public readonly extraData;

    /* istanbul ignore next */
    constructor(args: IAlternativeBotExceptionArgumentType) {
        const filteredArgs = {
            message: args.message,
        };
        super(filteredArgs);
        this.extraData = args.extraData;
        this.model = 'AlternativeBotException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): AlternativeBotException {
        return new AlternativeBotException({
            message: obj.message,
            extraData: obj.extraData,
        });
    }
}

/**
 * Base Exception for all WakeWord Exceptions.
 */
export class WakeWordException extends VoiceException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'WakeWordException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): WakeWordException {
        return new WakeWordException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when WakeWord action is valid, but unknown. This must never happen unless there is a code push,
 * and a version mismatch in packages.
 */
export class WakeWordValidUnknownActionException extends WakeWordException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'WakeWordValidUnknownActionException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): WakeWordValidUnknownActionException {
        return new WakeWordValidUnknownActionException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when the action for WakeWord is unknown.
 */
export class WakeWordUnknownActionException extends WakeWordException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'WakeWordUnknownActionException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): WakeWordUnknownActionException {
        return new WakeWordUnknownActionException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when the action is not valid for the state of WakeWord.
 */
export class WakeWordInvalidActionException extends WakeWordException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'WakeWordInvalidActionException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): WakeWordInvalidActionException {
        return new WakeWordInvalidActionException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when the WakeWord initialization not available.
 */
export class WakeWordInitializationUnavailableException extends WakeWordException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'WakeWordInitializationUnavailableException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): WakeWordInitializationUnavailableException {
        return new WakeWordInitializationUnavailableException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when the WakeWord fails to start listening for the wake word.
 */
export class WakeWordListeningUnavailableException extends WakeWordException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'WakeWordListeningUnavailableException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): WakeWordListeningUnavailableException {
        return new WakeWordListeningUnavailableException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when the WakeWord fails during the detection of wake word.
 */
export class WakeWordDetectionFailureException extends WakeWordException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'WakeWordDetectionFailureException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): WakeWordDetectionFailureException {
        return new WakeWordDetectionFailureException({
            message: obj.message,
        });
    }
}

/**
 * Unknown error exception in WakeWord.
 */
export class WakeWordUnknownErrorException extends WakeWordException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'WakeWordUnknownErrorException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): WakeWordUnknownErrorException {
        return new WakeWordUnknownErrorException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when listening to wake word is interrupted.
 */
export class WakeWordListeningInterruptedException extends WakeWordException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'WakeWordListeningInterruptedException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): WakeWordListeningInterruptedException {
        return new WakeWordListeningInterruptedException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when there is a timeout.
 */
export class WakeWordTimeoutException extends WakeWordException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'WakeWordTimeoutException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): WakeWordTimeoutException {
        return new WakeWordTimeoutException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when there is a timeout.
 */
export class ConfirmationDeclinedException extends VoiceException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'ConfirmationDeclinedException';
    }

    /**
     * Static `deserialize` method to the Constructor so that VoiceCrossFrameWrapper can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): ConfirmationDeclinedException {
        return new ConfirmationDeclinedException({
            message: obj.message,
        });
    }
}
